import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  useCurrent,
  useFetch,
  usePost,
  usePut,
  useUploadFile,
} from "../../hooks";
import { getDateNow } from "../../utils/global";
import useConverter from "../../hooks/useConverter";
import { useSelector } from "react-redux";
import { formatMoney } from "../../hooks/formatMoney";
import {
  CardHeadeWithActions,
  MwButton,
  MwInputFile,
  MwInputText,
  MwSelector,
  MwSpinner,
  MwSpinnerButton,
  MwTextArea,
  TagList,
} from "../../components/ui";
import { NewButton } from "../../components/ui/NewButton";
import i18next from "i18next";
import ExpensesFormListItem from "./ExpensesFormListItem";
import { FaWindowClose } from "react-icons/fa";
const ExpenseForm = ({ mode, setMode }) => {
  const params = useParams();
  const location = useLocation();
  const addDateParam = params?.date;
  const expenseId = params?.id;
  const hasValidExpenseId =
    Boolean(expenseId) && expenseId !== "null" && expenseId !== "undefined";
  const isReadOnlyView =
    new URLSearchParams(location.search).get("readonly") === "1";
  const isFormLocked = isReadOnlyView;
  const [t] = useTranslation("global");
  const navigate = useNavigate();
  const [minEditDelayDone, setMinEditDelayDone] = useState(mode !== "Editing");
  const {
    currentTenantId,
    currentCompanyId,
    currentBranchId,
    userId,
    currentLangId,
  } = useCurrent();

  const canFetchContext = Boolean(
    currentTenantId && currentCompanyId && currentBranchId
  );

  const baseFinanceUrl = (process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL || "").replace(
    /\/+$/,
    ""
  );
  const buildFinanceUrl = (path) =>
    `${baseFinanceUrl}/${String(path || "").replace(/^\/+/, "")}`;

  const uploadsBaseUrl = (process.env.REACT_APP_UPLOADS_API_BASE_URL || "").replace(/\/+$/, "");
  const toPublicFileUrl = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^(https?:)?\/\//i.test(raw) || /^(blob:|data:)/i.test(raw)) return raw;
    const pathOnly = raw.replace(/^\/+/, "");
    return uploadsBaseUrl ? `${uploadsBaseUrl}/${pathOnly}` : `/${pathOnly}`;
  };

  const { data: expensesData, loading: loadingexpenses } = useFetch(
    mode === "Editing" && hasValidExpenseId
      ? buildFinanceUrl(`expenses/${expenseId}`)
      : null,
    { cache: false, persist: false }
  );
  const { data: expensesTypeResult, loading: loadingexpensesResult } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/expenses-types/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null
  );

  const { data: treasuriesResult } = useFetch(
    canFetchContext && baseFinanceUrl
      ? buildFinanceUrl(
          `treasuries/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null
  );
  const { data: lastYearByTreasuryResult } = useFetch(
    canFetchContext && baseFinanceUrl
      ? buildFinanceUrl(
          `incomes/getLastYearByTreasury/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
        )
      : null,
    { cache: false, persist: false }
  );

  const { data: vendorsResult, loading: loadingTreasuriesResult } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_VENDORS_API_SERVER_BASE_URL}/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null
  );
  const [previewFile, setPreviewFile] = useState(null);

  const {
    data: expensesPostResult,
    loading: loadingPostResult,
    postData,
    codeError,
  } = usePost();
  const { data, putData, loading: putloading } = usePut();
  const [expensesTypes, setexpensesTypes] = useState([]);
  const today = getDateNow().toLocaleDateString("en-CA");
  const [errors, setErrors] = useState({});
  const {
    data: taxtype,
    loading: loadingtaxtype,
    error: taxesError,
    refreshHandler: refreshTaxes,
  } = useFetch(
    canFetchContext
      ? `${process.env.REACT_APP_VAT_WEB_BASE_URL}/sales/taxes/${currentTenantId}/${currentCompanyId}/${currentBranchId}`
      : null,
    { cache: false, persist: false }
  );
  const [expensesFormItemsState, setexpensesFormItemsState] = useState();
  useEffect(() => {
    if (!expensesFormItemsState || expensesFormItemsState.length === 0) {
      setexpensesFormItemsState([
        {
          to_account_no: "",
          to_account_name: "",
          desc: "",
          amount: "",
          tax_type: "",
          to_cost_account_no: "",
        },
      ]);
    }
  }, [expensesFormItemsState]);
  const [showAttachPopup, setShowAttachPopup] = useState(false);

  useEffect(() => {
    if (mode !== "Editing") {
      setMinEditDelayDone(true);
      return;
    }
    setMinEditDelayDone(false);
    const timer = setTimeout(() => setMinEditDelayDone(true), 1200);
    return () => clearTimeout(timer);
  }, [mode, expenseId]);

  const [expensesFormState, setexpensesFormState] = useState({
    code: "",
    total_amount: "",
    vat_amount: "",
    total_amount_with_vat: "",
    desc: "",
    attach: "",
    seller_name: "",
    tags: [],
    vendor_id: "",
    vendor_name: "",
    treasury_id_selected: "",
    tax_type_selected: {},
    repeat_period: "",
    repeat_expire: "",
    expense_date: today,
    expenses_type_id: "",
  });
  useEffect(() => {
    if (mode === "Editing") return;
    if (!addDateParam || !/^\d{4}-\d{2}-\d{2}$/.test(addDateParam)) return;
    setexpensesFormState((prev) =>
      prev.expense_date === addDateParam
        ? prev
        : { ...prev, expense_date: addDateParam }
    );
  }, [addDateParam, mode]);
  const formatYmd = (value) => {
    if (!value) return "";
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };
  const expenseDateForCode = formatYmd(expensesFormState.expense_date) || today;
  const { data: expensesCountResult, loading: loadingCountResult } = useFetch(
    canFetchContext && mode !== "Editing"
      ? `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/expenses/count/${currentTenantId}/${currentCompanyId}/${currentBranchId}/${encodeURIComponent(
          expenseDateForCode
        )}`
      : null
    ,
    { cache: false, persist: false }
  );
  useEffect(() => {
    if (expensesTypeResult) {
      const _Types = expensesTypeResult.data.map((item) => ({
        label: currentLangId === "ar" ? item.name_ar : item.name_en,
        value: item.id,
        lang: currentLangId,
      }));
      setexpensesTypes(_Types);
    }
  }, [expensesTypeResult, currentLangId]);

    const [_taxTypeList, set_taxTypeList] = useState([]);
    useEffect(() => {
      if (taxtype) {
        const _taxTypeList = taxtype.data.map((item) => ({
          label: currentLangId === "ar" ? item.tax_name_ar : item.tax_name_en,
          id: item.id,
          value: item.tax_value,
          lang: currentLangId,
        }));
        set_taxTypeList(_taxTypeList);
      }
    }, [currentLangId, taxtype]);
  const allowTypes = "*";

  const {
    location: attachLocation,
    loading: loadingAttach,
    handleMultiUploads: handleUpload,
  } = useUploadFile(allowTypes, `expenses-${currentTenantId}`, "file");

  const { resultdelete, loadingDeleting, handleDelete } = useUploadFile();

  const addNewHandler = () => {
    const lastItem = expensesFormItemsState[expensesFormItemsState.length - 1];
    let error = {};

    if (!lastItem || !lastItem.amount) {
      if (!lastItem?.amount || isNaN(parseFloat(lastItem?.amount))) {
        error.amount = "Amount Is Required";
      }
    }

    if (Object.keys(error).length > 0) {
      setErrors(error);
      return;
    }

    setErrors({});
    setexpensesFormItemsState((prevState) => [
      ...prevState,
      {
        to_account_no: "",
        to_account_name: "",
        desc: "",
        amount: "",
        tax_type: "",
        to_cost_account_no: "",
      },
    ]);
  };

  useEffect(() => {
    if (expensesCountResult && mode !== "Editing") {
      setexpensesFormState((prevState) => ({
        ...prevState,
        code: parseInt(expensesCountResult?.data),
      }));
    }
  }, [expensesCountResult, mode]);
  const [vendorsList, setVendorsList] = useState([]);
  useEffect(() => {
    if (vendorsResult?.data) {
      const _list = vendorsResult.data.map((item) => {
        const fullnameObj = item.fullname?.find((name) => name.lang === "ar");
        const label = fullnameObj?.text || "";
        return {
          label,
          value: item?._id || "",
          lang: currentLangId,
        };
      });
      setVendorsList(_list);
    }
  }, [vendorsResult, currentLangId]);

  const [treasuriesWithAccounts, setTreasuriesWithAccounts] = useState([]);

  useEffect(() => {
    if (treasuriesResult?.data) {
      // حفظ بيانات الخزائن مع حساباتها
      const _treasuriesWithAccounts = treasuriesResult.data.map((item) => ({
        id: item.id,
        name_ar: item.name_ar,
        name_en: item.name_en,
        account_no: item.account_no, // حساب الخزينة
      }));
      setTreasuriesWithAccounts(_treasuriesWithAccounts);
    }
  }, [treasuriesResult]);

  const treasuriesOptions = useMemo(() => {
    const locale = "en-US";
    const treasuryListRaw = treasuriesResult?.data ?? treasuriesResult ?? [];
    const byTreasuryPayload =
      lastYearByTreasuryResult?.data ?? lastYearByTreasuryResult ?? {};
    const incomesRaw = Array.isArray(byTreasuryPayload?.Incomes)
      ? byTreasuryPayload.Incomes
      : [];
    const expensesRaw = Array.isArray(byTreasuryPayload?.Expenses)
      ? byTreasuryPayload.Expenses
      : [];

    const incomesById = incomesRaw.reduce((acc, item) => {
      const id = item?.treasury_id ?? item?.treasury?.id;
      if (id == null) return acc;
      const key = String(id);
      acc[key] = (acc[key] || 0) + (Number(item?.total) || 0);
      return acc;
    }, {});
    const expensesById = expensesRaw.reduce((acc, item) => {
      const id = item?.treasury_id ?? item?.treasury?.id;
      if (id == null) return acc;
      const key = String(id);
      acc[key] = (acc[key] || 0) + (Number(item?.total) || 0);
      return acc;
    }, {});
    const netById = Object.keys({ ...incomesById, ...expensesById }).reduce(
      (acc, key) => {
        acc[key] = (incomesById[key] || 0) - (expensesById[key] || 0);
        return acc;
      },
      {}
    );

    const treasuryById = new Map();
    if (Array.isArray(treasuryListRaw)) {
      treasuryListRaw.forEach((t) => {
        if (t?.id == null) return;
        treasuryById.set(String(t.id), t);
      });
    }
    incomesRaw.forEach((i) => {
      const t = i?.treasury;
      if (!t || t?.id == null) return;
      treasuryById.set(String(t.id), t);
    });
    expensesRaw.forEach((e) => {
      const t = e?.treasury;
      if (!t || t?.id == null) return;
      treasuryById.set(String(t.id), t);
    });

    const list = Array.from(treasuryById.values()).map((t) => {
      const id = String(t.id);
      const name =
        currentLangId === "ar" ? t?.name_ar || "" : t?.name_en || "";
      const code = String(t?.code ?? "").trim();
      const balance = netById[id] || 0;
      const balanceLabel = formatMoney(balance, locale);
      const labelBase = code ? `${code} - ${name}` : name;
      return {
        label: `${labelBase} (${balanceLabel})`,
        value: t.id,
        lang: currentLangId,
      };
    });

    return list.sort((a, b) => {
      const av = String(a?.label || "");
      const bv = String(b?.label || "");
      return av.localeCompare(bv, locale);
    });
  }, [currentLangId, treasuriesResult, lastYearByTreasuryResult]);

  useEffect(() => {
    if (attachLocation) {
      setexpensesFormState((prev) => ({
        ...prev,
        attach: prev.attach
          ? `${prev.attach},${attachLocation}`
          : attachLocation,
      }));
    }
  }, [attachLocation]);


  useEffect(() => {
    if (!expensesFormItemsState?.length) return;

    const totalGrossAmount = expensesFormItemsState.reduce(
      (acc, item) => acc + (parseFloat(item.amount) || 0),
      0
    );

    const vatAmount = expensesFormItemsState.reduce(
      (acc, item) => {
        const grossAmount = parseFloat(item?.amount) || 0;
        const taxPercent = Number(item?.tax_type?.value || 0);
        const itemVat =
          taxPercent > 0
            ? (grossAmount * taxPercent) / (100 + taxPercent)
            : 0;
        return acc + itemVat;
      },
      0
    );
    const totalAmount = totalGrossAmount - vatAmount;

    setexpensesFormState((prevState) => ({
      ...prevState,
      total_amount: totalAmount.toFixed(2),
      vat_amount: vatAmount.toFixed(2),
      total_amount_with_vat: totalGrossAmount.toFixed(2),
    }));
  }, [expensesFormItemsState]);

  useEffect(() => {
    setTimeout(() => {
      if (expensesPostResult && codeError === 1) {
        if (mode === "Editing" && hasValidExpenseId) {
          navigate(`/${params.tenant}/expenses/show/${expenseId}`);
        } else {
          navigate(`/${params.tenant}/expenses`);
        }
      }
    }, 300);
  }, [expensesPostResult, codeError, mode, hasValidExpenseId, expenseId, navigate, params.tenant]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (mode === "Editing" && !hasValidExpenseId) {
      navigate(`/${params.tenant}/expenses`);
      return;
    }
    let errorObj = {};
    const lastIndex = expensesFormItemsState.length - 1;

    if (!expensesFormState?.treasury_id_selected) {
      errorObj = { ...errorObj, treasury: "treasury is required" };
    }
    if (!expensesFormState?.vendor_id) {
      errorObj = { ...errorObj, vendor: "vendor is required" };
    }

    if (!expensesFormItemsState[lastIndex]?.amount) {
      errorObj = { ...errorObj, amount: " Amount is required" };
    }
    if (expensesFormState.attach?.split(",").length > 5) {
      errorObj = { ...errorObj, attach: t("Salaries.NumberValidate") };
    }
    if (Object.keys(errorObj).length !== 0) {
      setErrors(errorObj);
      return;
    }

    const _dataPost = {
      code: expensesFormState.code,
      total_amount: parseFloat(expensesFormState?.total_amount || 0).toFixed(2),
      vat_amount: parseFloat(expensesFormState.vat_amount || 0).toFixed(2),
      total_amount_with_vat: parseFloat(
        expensesFormState.total_amount_with_vat || 0
      ).toFixed(2),
      desc: expensesFormState.desc || "",
      attach: expensesFormState.attach || "",
      seller_name:
        String(expensesFormState.seller_name || "").trim() ||
        String(expensesFormState.vendor_name || "").trim(),
      tags: JSON.stringify(expensesFormState.tags),
      vendor_id: expensesFormState.vendor_id,
      vendor_name: expensesFormState.vendor_name,
      treasury_id: expensesFormState.treasury_id_selected?.value,
      tax_type: expensesFormItemsState?.tax_type,
      repeat_period: expensesFormState.repeat_period || 0,
      repeat_expire: expensesFormState.repeat_expire || "",
      expense_date: expensesFormState.expense_date,
      tenant_id: currentTenantId,
      company_id: currentCompanyId,
      branch_id: currentBranchId,
      created_by: userId,
      expenses_type_id: expensesFormState?.expenses_type_id?.value || "",
      details: expensesFormItemsState.map((item) => {
        const grossAmount = parseFloat(item.amount || 0);
        const taxPercent = Number(item?.tax_type?.value || 0);
        const vatPortion =
          taxPercent > 0 ? (grossAmount * taxPercent) / (100 + taxPercent) : 0;
        const netAmount = grossAmount - vatPortion;
        return {
          to_account_no: item?.to_account_no || "",
          desc: item.desc || "",
          amount: Number.isFinite(netAmount) ? netAmount.toFixed(2) : "0.00",
          tax_type: item?.tax_type?.id || item?.tax_type || "",
          to_cost_account_no: item?.to_cost_account_no || "",
        };
      }),
    };
    if (mode === "Editing") {
      const redirectAction = `expenses/show/${expenseId}`;
      putData(
        `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/expenses/${expenseId}`,
        _dataPost,
        redirectAction
      );
    } else {
      postData(
        `${process.env.REACT_APP_FINANCE_API_SERVER_BASE_URL}/expenses`,
        _dataPost
      );
    }
  };

  useEffect(() => {
    if (
      expensesData
      // vendorsList.length > 0 &&
      // _taxTypeList.length > 0 &&
      // expensesTypes.length > 0
    ) {
      setexpensesFormState({
        code: expensesData?.data?.code || expensesFormState.code,
        total_amount: expensesData?.data?.total_amount || "",
        vat_amount: expensesData?.data?.vat_amount || "",
        total_amount_with_vat: expensesData?.data?.total_amount_with_vat || "",
        desc: expensesData?.data?.desc || "",
        attach: expensesData?.data?.attach || "",
        seller_name: expensesData?.data?.seller_name || "",
        tags: expensesData?.data?.tags
          ? JSON.parse(expensesData.data.tags)
          : [],
        vendor_id: expensesData?.data?.vendor_id,
        vendor_name: expensesData?.data?.vendor_name,
        treasury_id_selected:
          treasuriesOptions.find(
            (t) => String(t.value) === String(expensesData?.data?.treasury_id)
          ) || "",
        expenses_type_id:
          expensesTypes.find(
            (t) => t.value === expensesData?.data?.expenses_type_id
          ) || "",
        vendor_id_selected:
          vendorsList.find((t) => t.value === expensesData?.data?.vendor_id) ||
          "",
        tax_type_selected:
          _taxTypeList.find((t) => t.value === expensesData?.data?.tax_type) ||
          {},
        repeat_period: expensesData?.data?.repeat_period || "",
        repeat_expire: expensesData?.data?.repeat_expire || "",
        expense_date: expensesData?.data?.expense_date,
      });

      const detailsList = Array.isArray(expensesData?.data?.details)
        ? expensesData.data.details
        : [];
      const totalDetailsAmount = detailsList.reduce(
        (acc, item) => acc + Number(item?.amount || 0),
        0
      );
      const totalVatAmount = Number(expensesData?.data?.vat_amount || 0);
      
      setexpensesFormItemsState(
        detailsList.map((item) => {
          const rawTaxId = item?.tax_type?.id ?? item?.tax_type?._id ?? item?.tax_type;
          const rawTaxValue = item?.tax_type?.value ?? item?.tax_type?.tax_value;
          const rawTaxLabel = item?.tax_type?.label ?? item?.tax_type?.tax_name_ar ?? item?.tax_type?.tax_name_en;
          const totalAmountNumber = Number(expensesData?.data?.total_amount || 0);
          const totalVatNumber = Number(expensesData?.data?.vat_amount || 0);
          const fallbackTaxPercent =
            totalAmountNumber > 0 && totalVatNumber > 0
              ? Number(((totalVatNumber / totalAmountNumber) * 100).toFixed(4))
              : 0;
          const matchedTax =
            _taxTypeList.find((t) => String(t.id) === String(rawTaxId)) ||
            _taxTypeList.find((t) => String(t.value) === String(rawTaxValue)) ||
            _taxTypeList.find((t) => String(t.label || "").trim() === String(rawTaxLabel || "").trim()) ||
            (fallbackTaxPercent > 0
              ? {
                  label: fallbackTaxPercent.toString(),
                  id: "",
                  value: fallbackTaxPercent,
                  lang: currentLangId,
                }
              : "");
          const amountNumber = Number(item?.amount || 0);
          const directTaxAmount = item?.vat_amount ?? item?.tax_amount;
          const distributedTaxAmount =
            totalDetailsAmount > 0
              ? Number(((amountNumber / totalDetailsAmount) * totalVatAmount).toFixed(2))
              : totalVatAmount;
          const taxAmountValue =
            directTaxAmount !== undefined &&
            directTaxAmount !== null &&
            String(directTaxAmount).trim() !== ""
              ? Number(directTaxAmount)
              : distributedTaxAmount;
          
          // Handle to_account_no format from API
          let toAccountNoValue = "";
          let toAccountNameValue = "";
          if (item.to_account_no) {
            if (typeof item.to_account_no === 'object' && item.to_account_no.account_no) {
              // If it's an object with account_no
              toAccountNoValue = item.to_account_no.account_no;
              toAccountNameValue = currentLangId === "ar" 
                ? (item.to_account_no.name_ar || item.to_account_no.name_en || "")
                : (item.to_account_no.name_en || item.to_account_no.name_ar || "");
            } else if (typeof item.to_account_no === 'string') {
              // If it's a string, keep it as is
              toAccountNoValue = item.to_account_no;
            }
          }
          
          return {
            to_account_no: toAccountNoValue,
            to_account_name: toAccountNameValue,
            desc: item.desc || "",
            amount: Number(
              (Number(item.amount || 0) + Number(taxAmountValue || 0)).toFixed(2)
            ).toString(),
            tax_type: matchedTax,
            tax_amount_value: Number.isFinite(taxAmountValue)
              ? taxAmountValue.toFixed(2)
              : "0.00",
            to_cost_account_no: item.to_cost_account_no || "",
          };
        }) || []
      );
    }
  }, [expensesData, vendorsList, _taxTypeList, expensesTypes, treasuriesOptions]);

  const { convertNumberToWords } = useConverter();
  const [totalAmountInWords, setTotalAmountInWords] = useState("");
  const language = useSelector((state) => state.lang.language);
  const manageexpenses = () => {
    navigate(`/${params.tenant}/expenses`);
  };

  // الحصول على حساب الخزينة المختار
  const selectedTreasuryAccount = treasuriesWithAccounts.find(
    t => t.id === expensesFormState.treasury_id_selected?.value
  );
  useEffect(() => {
    setTotalAmountInWords(
      convertNumberToWords(expensesFormState.total_amount_with_vat)
    );
  }, [expensesFormState.total_amount_with_vat, language]);
  return (
    <>
      {mode === "Editing" &&
      (!minEditDelayDone || loadingexpenses || loadingtaxtype) ? (
        <div className="bg-white h-[60vh] flex items-center justify-center">
          <MwSpinner hightScreen={false} />
        </div>
      ) : taxesError && _taxTypeList.length === 0 ? (
        <div className="h-[70vh] w-full flex flex-col gap-3 justify-center items-center">
          <div className="text-[#261759]">
            {taxesError?.response?.data?.message || taxesError?.message}
          </div>
          <NewButton type="saveBtn" onClick={refreshTaxes}>
            إعادة المحاولة
          </NewButton>
        </div>
      ) : (
        <div className="p-4 flex flex-col gap-5 bg-white">
          <CardHeadeWithActions
            title={
              mode !== "Editing" ? (
                <p className="text-[#261759]">{t("expenses.addexpenses")}</p>
              ) : (
                <p className="text-[#261759]">{t("expenses.editexpenses")}</p>
              )
            }
          >
            <div className="flex gap-4 w-72 justify-end items-center">
              <NewButton type="" onClick={manageexpenses}>
                <img
                  className="w-5"
                  src="/assets/icons/Vector(10).png"
                  alt="manage icon"
                />
                <p className="text-[13px] ms-1">
                  {t("dropdown.manageexpenses")}
                </p>
              </NewButton>
              <NewButton
                onClick={submitHandler}
                disabled={
                  isReadOnlyView ||
                  expensesFormState.total_amount <= 0 ||
                  putloading ||
                  loadingPostResult
                }
                type="saveBtn"
              >
                {loadingPostResult || putloading ? (
                  <MwSpinnerButton />
                ) : (
                  <span>
                    {mode === "Editing"
                      ? t("dropdown.save")
                      : t("dropdown.save")}
                  </span>
                )}
              </NewButton>
            </div>
          </CardHeadeWithActions>
          <div className="bg-white  p-0 flex flex-row gap-3 border-b border-gray-200">
            <div className="border-e pe-3 flex-1 flex flex-col gap-3">
              <div className="flex gap-2 ">
                <div className="w-40">
                  <MwInputText
                    classNameInput="border border-[#8D82B6] text-center border-opacity-55 outline-none"
                    label={t("expenses.expensesNumber")}
                    invalid={codeError === 0}
                    invalidMsg={codeError === 0 && t("Salaries.codeError")}
                    value={expensesFormState?.code}
                    disabled={isFormLocked}
                    onChange={(e) =>
                      setexpensesFormState({
                        ...expensesFormState,
                        code: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="w-42">
                  <MwInputText
                    inputType="text"
                    disabled={true}
                    label={t("expenses.expensesAmount")}
                    classNameInput=" border text-center border-[#8D82B6] border-opacity-55 text-[#261759]  text-xl text-lg outline-none"
                    value={expensesFormState?.total_amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d*\.?\d*$/.test(value)) {
                        setexpensesFormState({
                          ...expensesFormState,
                          total_amount: value,
                        });
                      }
                    }}
                  />
                </div>
                <div className="w-56">
                  <MwSelector
                    label={t("invoice.Treasury")}
                    initalValue={t("invoice.selectTreasury")}
                    invalid={!!errors?.treasury}
                    invalidMsg={errors?.treasury}
                    dataType={"labelValue"}
                    _data={treasuriesOptions}
                    withAddNew={false}
                    withSearch={true}
                    disabled={isFormLocked}
                    selectedItem={expensesFormState.treasury_id_selected}
                    setSelectedItem={(newValue) =>
                      setexpensesFormState({
                        ...expensesFormState,
                        treasury_id_selected: newValue,
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <MwSelector
                    label={t("expenses.vendor")}
                    initalValue={t("invoice.selectVendor")}
                    invalid={!!errors?.vendor}
                    invalidMsg={errors?.vendor}
                    dataType={"labelValue"}
                    _data={vendorsList}
                    withAddNew={false}
                    withSearch={true}
                    disabled={isFormLocked}
                    selectedItem={vendorsList.find(
                      (vendor) => vendor.value === expensesFormState.vendor_id
                    )}
                    setSelectedItem={(newValue) =>
                      setexpensesFormState((prev) => {
                        const nextVendorName = String(newValue?.label || "").trim();
                        const prevSeller = String(prev?.seller_name || "").trim();
                        const prevVendor = String(prev?.vendor_name || "").trim();
                        const shouldSyncSeller =
                          !prevSeller || prevSeller === prevVendor;
                        return {
                          ...prev,
                          vendor_id: newValue.value,
                          vendor_name: newValue.label,
                          seller_name: shouldSyncSeller
                            ? nextVendorName
                            : prev.seller_name,
                        };
                      })
                    }
                  />
                </div>
                <div className="flex-1">
                  <MwInputText
                    inputType="date"
                    classNameInput="border border-[#8D82B6] border-opacity-55 text-md text-[#261759] color-[red-800] outline-none"
                    label={t("expenses.expensesDate")}
                    value={expensesFormState?.expense_date}
                    disabled={isFormLocked}
                    onChange={(e) =>
                      setexpensesFormState({
                        ...expensesFormState,
                        expense_date: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
              <div className="flex-1">
                <MwTextArea
                  rows={4}
                  classNamePrivate="border bg-red border-[#8D82B6] border-opacity-55 min-h-32 outline-none"
                  label={t("expenses.expensesDescription")}
                  placeholder={t("expenses.desc") + "..."}
                  value={expensesFormState.desc}
                  disabled={isFormLocked}
                  onChange={(e) =>
                    setexpensesFormState({
                      ...expensesFormState,
                      desc: e.target.value,
                    })
                  }
                />
                {mode && (
                  <>
                    <h2 className="  text-[#261759] mb-2 font-bold flex flex-col w-full  mt-3 justify-between  items-start ">
                      <span className="font-semibold   text-[#261759] ">
                        <span className="font-bold text-sm    text-[#261759] ">
                          {t("Salaries.total-arabic")} :
                        </span>
                        <div className="total-arab flex flex-col gap-1 mt-1 justify-end items-en">
                          <span className="text-sm  font-semibold  text-[#261759] ">
                            {language === "ar"
                              ? totalAmountInWords
                              : convertNumberToWords(
                                expensesFormState.total_amount,
                                "ar"
                              )}
                            {" - "}
                            <span>فقط لا غير</span>
                          </span>

                          <span className="text-sm  font-semibold  text-[#261759] ">
                            {language === "en"
                              ? totalAmountInWords
                              : convertNumberToWords(
                                expensesFormState.total_amount,
                                "en"
                              )}
                            {" , "}
                            <span>nothing more </span>
                          </span>
                        </div>
                      </span>
                    </h2>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-3 ">
              <div className="w-80   text-[#261759] ">
                  <MwInputText
                    label={t("expenses.saler")}
                    value={expensesFormState.seller_name || ""}
                  disabled={isFormLocked}
                  classNameInput="border border-[#8D82B6] border-opacity-55 outline-none"
                  placeholder={t("expenses.saler") + "..."}
                  onChange={(e) =>
                    setexpensesFormState({
                      ...expensesFormState,
                      seller_name: e.target.value,
                    })
                  }
                />
              </div>
              <div className="w-80   text-[#261759]  ">
                <TagList
                  withLabel={true}
                  label={t("expenses.tags")}
                  classNamePrivate="border border-[#8D82B6] border-opacity-55 outline-none"
                  tagList={expensesFormState.tags}
                  disabled={isFormLocked}
                  setTagList={(newValue) =>
                    setexpensesFormState({
                      ...expensesFormState,
                      tags: newValue,
                    })
                  }
                />
              </div>
              <div className="w-80   text-[#261759]  ">
                <MwSelector
                  label={t("expenses.expensesType")}
                  initalValue={t("expenses.selectExpensesType")}
                  dataType={"labelValue"}
                  _data={expensesTypes}
                  withAddNew={false}
                  withSearch={false}
                  disabled={isFormLocked}
                  selectedItem={expensesFormState.expenses_type_id}
                  setSelectedItem={(newValue) =>
                    setexpensesFormState({
                      ...expensesFormState,
                      expenses_type_id: newValue,
                    })
                  }
                />
              </div>

              <div className="w-[330px] gap-2 items-center">
  {/* ==== Attachments Preview ==== */}
  {expensesFormState?.attach ? (
    <div className="flex flex-wrap justify-start gap-4 mt-3">
      {expensesFormState.attach.split(",").map((file, index) => {
        const trimmedFile = file.trim();
        if (!trimmedFile) return null;
        const fileUrl = toPublicFileUrl(trimmedFile);
        const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(trimmedFile);

        return (
          <div
            key={index}
            className="relative group border border-[#8D82B6] rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all w-24 h-24 flex items-center justify-center bg-gray-50"
          >
            {isImage ? (
              <img
                src={fileUrl}
                alt={`attachment-${index}`}
                className="object-cover w-full h-full cursor-pointer group-hover:opacity-75 transition"
                onClick={() => {
                  setPreviewFile(fileUrl);
                  setShowAttachPopup(true);
                }}
              />
            ) : (
              <div
                className="flex flex-col items-center justify-center gap-1 cursor-pointer w-full h-full bg-white"
                onClick={() => {
                  setPreviewFile(fileUrl);
                  setShowAttachPopup(true);
                }}
              >
                <iframe
                  src={fileUrl}
                  title="Preview PDF"
                  className="w-[80%] h-[60%] border-none pointer-events-none"
                />
                <p className="text-[10px] text-[#261759] font-semibold">PDF</p>
              </div>
            )}

            {/* ==== Delete button ==== */}
            {!isFormLocked && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const updatedFiles = expensesFormState.attach
                    .split(",")
                    .filter((_, i) => i !== index)
                    .join(",");

                  setexpensesFormState((prev) => ({
                    ...prev,
                    attach: updatedFiles,
                  }));

                  handleDelete(trimmedFile);
                }}
                className="absolute top-1 right-1 bg-[#261759] text-white text-xs p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition"
                title="Delete file"
              >
                ✕
              </button>
            )}
          </div>
        );
      })}
    </div>
  ) : null}

  {/* ==== Popup Preview ==== */}
  {showAttachPopup && previewFile && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
      onClick={() => setShowAttachPopup(false)}
    >
      <div
        className="relative bg-white rounded-xl shadow-xl max-w-[90vw] max-h-[90vh] w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setShowAttachPopup(false)}
          className="absolute top-2 end-3 bg-[#261759] text-white rounded-full h-9 w-9 flex items-center justify-center text-xl z-50"
        >
          <FaWindowClose />
        </button>

        <div className="p-4 flex justify-center items-center max-h-[85vh] overflow-auto">
          {/\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(previewFile) ? (
            <img
              src={previewFile}
              alt="Preview"
              className="max-w-full max-h-[80vh] object-contain rounded-md"
            />
          ) : (
            <iframe
              src={previewFile}
              title="Preview PDF"
              className="w-[80vw] h-[80vh] border-none"
            />
          )}
        </div>
      </div>
    </div>
  )}

  {/* ==== Upload Input ==== */}
  {!isFormLocked && (
    <div className="mt-3">
      <MwInputFile
        isMultiple={true}
        label={t("Salaries.sentFiels") + " :"}
        loading={loadingAttach}
        acceptTypes="*/*"
        onChangeHandler={(newFiles) => {
          handleUpload(newFiles);
        }}
      />
    </div>
  )}

  {/* ==== Error ==== */}
  {errors.attach && (
    <div className="!text-red-500 text-sm mt-1 text-center">
      {errors.attach}
    </div>
  )}
</div>



            </div>
          </div>

          <div className="bg-white rounded-2xl p-0">
            <div className="text-[#261759] font-bold mb-2 p-0">
              {t("Salaries.accounts")}
            </div>
            {expensesFormItemsState && (
              <div className="bg-white rounded-2xl p-0 ">
                {expensesFormItemsState.map((item, index) => (
                  <ExpensesFormListItem
                    errors={errors}
                    index={index}
                    item={item}
                    key={index}
                    expensesFormItemsState={expensesFormItemsState}
                    setexpensesFormItemsState={setexpensesFormItemsState}
                    _taxTypeList={_taxTypeList}
                    treasuiesList={vendorsList}
                    addNewHandler={addNewHandler}
                    treasuryAccount={selectedTreasuryAccount}
                    disabledAll={isReadOnlyView}
                  />
                ))}
              </div>
            )}
            {/* <div className="flex flex-row-reverse">
              <div className="flex w-max flex-row-reverse me-4  gap-2 mt-0">
                <NewButton type="newBtn" onClick={addNewHandler}>
                  {t("dropdown.add")}
                </NewButton>
              </div>
            </div> */}
          </div>
        </div>
      )}
    </>
  );
};

export default ExpenseForm;
